/**
 * Cross-reference derivation for the Sources panel + document viewer (#20423).
 *
 * The backend emits claim-level provenance (`AssistantResponseSources.claims`):
 * each claim is a span of assistant prose plus the `chunk_ids` cited for it.
 * When one claim cites chunks from MORE THAN ONE document, those sources are
 * "cross-referenced" — they support the same statement from different documents
 * (e.g. an FSAR section and the NRC Reg Guide it conforms to). This module turns
 * that claim data into:
 *
 *  - {@link buildClaimClusters}: cited sources grouped for the Sources panel —
 *    ONLY cross-reference claims (spanning >1 document) form headed groups
 *    (chat-v2 mockup); every other cited source is a flat headingless
 *    singleton, so a response with no cross-references reads as a plain list.
 *  - {@link getCrossReferences}: for one cited chunk, the sibling cited sources
 *    in OTHER documents that share a claim with it.
 *  - {@link attachAnnotationCrossRefs}: stamps that sibling data onto the
 *    document viewer's cited annotations, where it renders as inline
 *    "↔ Cross-referenced with [N]" jump links under each highlight (chat-v2).
 *
 * Citation numbers mirror `rehypeWrapCite` / `buildDocumentAnnotations`: built
 * through `mergeCitedWithUnavailable`, so a cited source keeps its stable slot
 * number even when other cited documents become unavailable (plain 1-based
 * index when there are no unavailable entries).
 */
import type {
  AssistantResponseSources,
  ProvenanceSource,
} from "../../types/provenance";
import type {
  AnnotationCrossRef,
  DocumentAnnotation,
} from "src/views/datasets/documentAnnotations";
import { recordHome, recordTitle, type RecordHome } from "src/api/records";
import {
  buildDocumentAnnotations,
  resolveContentPage,
} from "./buildDocumentAnnotations";
import { mergeCitedWithUnavailable } from "./citedTierMerge";

/** A cited source with everything the cross-reference UI needs to render + jump. */
export interface CitedSourceRef {
  source: ProvenanceSource;
  /** Gap-aware slot number (the inline citation number). */
  citationNumber: number;
  chunkId: number;
  sdrId: number | undefined;
  title: string;
}

/** A group of cited sources that support the same claim (or an ungrouped singleton). */
export interface CrossReferenceCluster {
  members: CitedSourceRef[];
  /**
   * Claim heading shown above the group: the claim's short `label` when present,
   * else its `claim_text` sentence. Unset for cited sources not tied to any
   * claim — those render as a flat card with no heading.
   */
  heading?: string;
  /** True when the cluster's members span more than one document (a cross-reference). */
  isCrossReference: boolean;
}

/** Human title for a source: extracted title, else file name, else dataset name, else a generic label. */
export const titleOf = (source: ProvenanceSource): string =>
  recordTitle(source.source_data_record) ??
  source.dataset?.name ??
  "Source document";

/**
 * Strip inline markdown emphasis from a claim heading. Claim labels/sentences
 * are extracted from the assistant's markdown prose, so they can carry `**bold**`,
 * `*em*`, `_em_`, or `` `code` `` markers. The panel heading renders as plain
 * text, so a tabular row whose claim reads "**Event Number:** 54423" would show
 * the literal asterisks — strip the markers instead of rendering them raw.
 */
export const stripInlineMarkdown = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/(^|\s)_(.+?)_(?=\s|$)/g, "$1$2");

/** Resolve cited sources into {@link CitedSourceRef}s (citation order). */
const buildCitedRefs = (
  provenance: AssistantResponseSources | null | undefined,
): CitedSourceRef[] => {
  const merged = mergeCitedWithUnavailable(
    provenance?.assistant_response_sources ?? [],
    (provenance?.unavailable_sources ?? []).filter((u) => u.cited),
  );
  const refs: CitedSourceRef[] = [];
  for (const entry of merged) {
    if (entry.kind !== "source") continue;
    const chunkId = entry.source.data_chunk?.id;
    if (chunkId == null) continue;
    refs.push({
      source: entry.source,
      citationNumber: entry.citationNumber,
      chunkId,
      sdrId: entry.source.source_data_record?.id,
      title: titleOf(entry.source),
    });
  }
  return refs;
};

/** Everything the viewer/store needs to open a source, wherever it lives. */
export interface SourceOpenTarget {
  home: RecordHome;
  sdrId: number;
  page: number | null;
  chunkId: number | null;
  annotations: DocumentAnnotation[];
  provenance: AssistantResponseSources | null;
}

/**
 * Build the open-target payload for a source: its resolved home (dataset or
 * project folder) + SDR to show, the densest content page, the clicked chunk,
 * and ALL of that document's annotations (cited + read). Carries `provenance`
 * so the viewer can keep deriving cross-references (and jump again) after
 * opening. Returns null when the source has no resolvable home (e.g. a pure
 * external link), which callers open by another route. Shared by the Sources
 * panel and the viewer's cross-reference jumps so both open sources
 * identically.
 */
export const buildSourceOpenTarget = (
  source: ProvenanceSource,
  provenance: AssistantResponseSources | null | undefined,
): SourceOpenTarget | null => {
  const sdr = source.source_data_record;
  const sdrId = sdr?.id;
  const home = recordHome({
    dataset_fk: sdr?.dataset_fk,
    project_folder_fk: sdr?.project_folder_fk,
  });
  if (!home || sdrId == null) return null;
  return {
    home,
    sdrId,
    page: resolveContentPage(source) ?? null,
    chunkId: source.data_chunk?.id ?? null,
    annotations: buildDocumentAnnotations(provenance, sdrId),
    provenance: provenance ?? null,
  };
};

/**
 * Cited sources in OTHER documents that share a claim with `chunkId` — the
 * cross-reference siblings. Returns [] when the chunk has no cross-document
 * co-citation (the common case), so callers render nothing.
 */
export const getCrossReferences = (
  provenance: AssistantResponseSources | null | undefined,
  chunkId: number | null | undefined,
): CitedSourceRef[] => {
  if (chunkId == null || !provenance?.claims?.length) return [];
  const refs = buildCitedRefs(provenance);
  const byChunk = new Map<number, CitedSourceRef>(
    refs.map((r) => [r.chunkId, r]),
  );
  const self = byChunk.get(chunkId);
  if (!self) return [];

  const siblings = new Map<number, CitedSourceRef>();
  for (const claim of provenance.claims) {
    const ids = claim.chunk_ids ?? [];
    if (!ids.includes(chunkId)) continue;
    for (const otherId of ids) {
      if (otherId === chunkId) continue;
      const other = byChunk.get(otherId);
      // A cross-reference relates DIFFERENT documents; skip same-document
      // passages (those aren't "cross"-references) and already-seen siblings.
      if (!other || other.sdrId === self.sdrId) continue;
      if (!siblings.has(other.chunkId)) siblings.set(other.chunkId, other);
    }
  }
  return [...siblings.values()].sort(
    (a, b) => a.citationNumber - b.citationNumber,
  );
};

/** Sibling passage's section/section_title for the xref label, when present. */
const sectionOf = (source: ProvenanceSource): string | undefined => {
  const meta = source.data_chunk?.chunk_metadata as
    | { section?: unknown; section_title?: unknown }
    | undefined;
  const raw = meta?.section ?? meta?.section_title;
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.replace(/^§\s*/, "").trim();
  return trimmed.length ? trimmed : undefined;
};

/**
 * Attach cross-reference jump data to each CITED annotation (chat-v2 mockup):
 * the viewer renders "↔ Cross-referenced with [N] Title · §Section" under the
 * highlight, linking to the sibling passage in the OTHER document cited for
 * the same claim. Read annotations and passages with no cross-document
 * co-citation pass through untouched; with no claim data the input is returned
 * as-is (identity), so documents without cross-references render exactly as
 * before.
 */
export const attachAnnotationCrossRefs = (
  annotations: DocumentAnnotation[],
  provenance: AssistantResponseSources | null | undefined,
): DocumentAnnotation[] => {
  if (!annotations.length || !provenance?.claims?.length) return annotations;
  return annotations.map((ann) => {
    if (!ann.cited) return ann;
    const chunkId =
      typeof ann.chunk_id === "string" ? Number(ann.chunk_id) : ann.chunk_id;
    const siblings = getCrossReferences(provenance, chunkId);
    if (!siblings.length) return ann;
    const crossRefs: AnnotationCrossRef[] = siblings.map((sib) => ({
      chunkId: sib.chunkId,
      citationNumber: sib.citationNumber,
      title: sib.title,
      ...(sectionOf(sib.source) ? { section: sectionOf(sib.source) } : {}),
    }));
    return { ...ann, crossRefs };
  });
};

/**
 * Group the Cited tier for the Sources panel (chat-v2 mockup): ONLY
 * cross-reference claims — claims whose cited sources span more than one
 * document — render as a headed group (the claim's short label, or its
 * sentence when no label yet). Every other cited source renders as a flat,
 * headingless singleton: a single-document claim isn't a *cross* reference,
 * and heading every citation buried the signal the grouping exists to carry.
 * A source is assigned to the FIRST claim whose remaining members still span
 * >1 document, so it appears exactly once and every rendered group is a
 * visible cross-reference. Clusters are ordered by their lowest citation
 * number; members within a cluster stay in citation order.
 */
export const buildClaimClusters = (
  provenance: AssistantResponseSources | null | undefined,
): CrossReferenceCluster[] => {
  const refs = buildCitedRefs(provenance);
  if (refs.length === 0) return [];

  const byChunk = new Map<number, CitedSourceRef>(
    refs.map((r) => [r.chunkId, r]),
  );
  const assigned = new Set<number>();
  const clusters: CrossReferenceCluster[] = [];

  for (const claim of provenance?.claims ?? []) {
    const members: CitedSourceRef[] = [];
    for (const cid of claim.chunk_ids ?? []) {
      if (assigned.has(cid)) continue;
      const ref = byChunk.get(cid);
      if (!ref) continue;
      members.push(ref);
    }
    // Group only when what's LEFT of the claim still crosses documents —
    // members consumed by an earlier group can demote a claim to flat.
    if (new Set(members.map((m) => m.sdrId)).size < 2) continue;
    members.forEach((m) => assigned.add(m.chunkId));
    members.sort((a, b) => a.citationNumber - b.citationNumber);
    const rawHeading = claim.label?.trim() || claim.claim_text?.trim();
    const heading = rawHeading ? stripInlineMarkdown(rawHeading) : undefined;
    clusters.push({ members, heading, isCrossReference: true });
  }

  // Everything else: flat, headingless singletons.
  for (const ref of refs) {
    if (assigned.has(ref.chunkId)) continue;
    assigned.add(ref.chunkId);
    clusters.push({ members: [ref], isCrossReference: false });
  }

  // Order top-down in citation order.
  clusters.sort(
    (a, b) => a.members[0].citationNumber - b.members[0].citationNumber,
  );
  return clusters;
};
